import React, { RefObject } from "react";
import { Box, CircularProgress, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  videoContainerStyles,
  videoStyles,
  loadingProgressStyles,
  initializingContainerStyles,
  loaderStyles,
} from "./styles";
import { NoiseWarningModal } from "../../noise-warning-modal/noise-warning-modal";

interface AvatarVideoDisplayProps {
  videoRef: RefObject<HTMLVideoElement | null>;
  isConnecting: boolean;
  isInitializing: boolean;
  isConnected: boolean;
  warningMessage: string | null;
  isWarningError: boolean;
  onWarningContinue: () => Promise<void>;
  onWarningClose: () => void;
  isRolePlay?: boolean;
  preferContainedFraming?: boolean;
}

export const AvatarVideoDisplay: React.FC<AvatarVideoDisplayProps> = ({
  videoRef,
  isConnecting,
  isInitializing,
  isConnected,
  warningMessage,
  isWarningError,
  onWarningContinue,
  onWarningClose,
  isRolePlay = false,
  preferContainedFraming = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isPhoneLandscape = useMediaQuery(
    "(orientation: landscape) and (max-height: 500px) and (pointer: coarse)",
  );
  const useCoverFit =
    preferContainedFraming || (isRolePlay && isPhoneLandscape);
  // Session layout: cover the rounded frame so waves sit on video, not a black letterbox.
  const objectFit: React.CSSProperties["objectFit"] = useCoverFit
    ? "cover"
    : isMobile
      ? "contain"
      : "cover";

  return (
    <Box sx={videoContainerStyles}>
      {isConnecting || isInitializing ? (
        <Box sx={initializingContainerStyles}>
          <CircularProgress sx={loadingProgressStyles} />
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.88)" }}>
            {isInitializing
              ? "Initializing session..."
              : "Connecting to avatar..."}
          </Typography>
          <Typography
            variant="caption"
            sx={{ mt: 1, color: "rgba(255,255,255,0.65)" }}
          >
            {isInitializing
              ? "Requesting microphone access and connecting to server..."
              : "Waiting for LiveKit connection data..."}
          </Typography>
        </Box>
      ) : (
        <>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={false}
            style={{
              ...videoStyles(isConnected),
              objectFit,
              objectPosition: preferContainedFraming
                ? "center 20%"
                : "center",
            }}
          />
          {!isConnected && (
            <Box sx={loaderStyles}>
              <CircularProgress size={60} sx={loadingProgressStyles} />
            </Box>
          )}
          <NoiseWarningModal
            open={!!(warningMessage && isWarningError)}
            message={warningMessage}
            toast={isRolePlay}
            autoDismissMs={3000}
            onContinue={() => {
              void onWarningContinue();
            }}
            onClose={onWarningClose}
          />
        </>
      )}
    </Box>
  );
};
